//Import api functions
import axios from "axios";
import { Cookies } from "react-cookie";
import { Capacitor } from "@capacitor/core";
import oauthConfig from "../../oauth.config";
import { login, postForgotPassword } from "./functions/login";
import useTest from "./functions/test";

const CookiesClass = new Cookies();

// Try to get tokens from Capacitor Preferences (if plugin is installed), else fallback to cookies
async function getAuthTokens() {
  let oauth_token = null;
  let oauth_secret = null;
  try {
    // Dynamically import; succeeds only if @capacitor/preferences is installed
    const { Preferences } = await import("@capacitor/preferences");
    const [t, s] = await Promise.all([
      Preferences.get({ key: "oauth_token" }),
      Preferences.get({ key: "oauth_secret" }),
    ]);
    oauth_token = t?.value || null;
    oauth_secret = s?.value || null;
  } catch (_) {
    // plugin not available; ignore
  }
  if (!oauth_token || !oauth_secret) {
    const cookies = CookiesClass.getAll();
    oauth_token = oauth_token || cookies["oauth_token"] || null;
    oauth_secret = oauth_secret || cookies["oauth_secret"] || null;
  }
  return { oauth_token, oauth_secret };
}

function addOauthToUrl(url, headers) {
  const u = new URL(url);
  if (headers?.["oauth_consumer_key"]) {
    u.searchParams.append("oauth_consumer_key", headers["oauth_consumer_key"]);
  }
  if (headers?.["oauth_consumer_secret"]) {
    u.searchParams.append(
      "oauth_consumer_secret",
      headers["oauth_consumer_secret"]
    );
  }
  if (headers?.["oauth_token"]) {
    u.searchParams.append("oauth_token", headers["oauth_token"]);
  }
  if (headers?.["oauth_secret"]) {
    u.searchParams.append("oauth_secret", headers["oauth_secret"]);
  }
  return u.toString();
}

function isFormData(val) {
  return val && typeof val.append === "function";
}

// SWR- and direct-call-compatible fetcher (Capacitor-aware)
export const fetcher = async (...args) => {
  // Normalize args: support fetcher([url, params], multipart?, json_req?, onProgress?)
  // and fetcher(url, params, multipart?, json_req?, onProgress?)
  let request_obj = null;
  let multipart = true;
  let json_req = false;
  let onProgress = () => {};

  if (Array.isArray(args[0])) {
    // Called by SWR: fetcher([url, params]) or direct fetcher([url, params], flags)
    request_obj = args[0];
    if (typeof args[1] === "boolean") multipart = args[1];
    if (typeof args[2] === "boolean") json_req = args[2];
    if (typeof args[3] === "function") onProgress = args[3];
  } else {
    // Called directly: fetcher(url, params, flags)
    request_obj = [args[0], args[1]];
    if (typeof args[2] === "boolean") multipart = args[2];
    if (typeof args[3] === "boolean") json_req = args[3];
    if (typeof args[4] === "function") onProgress = args[4];
  }

  let url = request_obj?.[0];
  let params = { ...(request_obj?.[1] || {}) };
  params.method = params.method || "GET";
  params.headers = { ...(params.headers || {}) };

  // Fix for appending GET request body to url (legacy behavior)
  if (params.method === "GET") {
    if (params.body) {
      url = url + "?" + params.body;
    }
    delete params.body;
  }

  // Inject oauth headers from storage (Preferences on native, cookies on web)
  const { oauth_token, oauth_secret } = await getAuthTokens();
  if (oauth_token) params.headers["oauth_token"] = oauth_token;
  if (oauth_secret) params.headers["oauth_secret"] = oauth_secret;

  // Manipulate query to include required oauth parameters for GET requests
  if (params.method === "GET") {
    url = addOauthToUrl(url, params.headers);
  }

  const isWrite = ["POST", "PUT", "DELETE"].includes(params.method);
  const bodyIsFormData = isFormData(params.body);

  if (isWrite && multipart && bodyIsFormData) {
    // Move tokens into multipart body (server expects in body)
    delete params.headers["oauth_token"];
    delete params.headers["oauth_secret"];
    if (oauth_token) params.body.append("oauth_token", oauth_token);
    if (oauth_secret) params.body.append("oauth_secret", oauth_secret);
    params.body.append("oauth_consumer_key", oauthConfig.oauth_consumer_key);
    params.body.append(
      "oauth_consumer_secret",
      oauthConfig.oauth_consumer_secret
    );
  }

  // JSON mode: add oauth to URL and prefer application/json responses
  if (json_req === true) {
    params.headers["Accept"] = "application/json";
    url = addOauthToUrl(url, params.headers);
  }

  // Platform detection for optional native HTTP usage (bypass CORS on device)
  const platform = Capacitor.getPlatform ? Capacitor.getPlatform() : "web";
  const isNative = platform !== "web";

  // Use Capacitor HTTP for non-multipart JSON requests on native (uploads stay on axios for progress support)
  if (isNative && isWrite && !bodyIsFormData && json_req) {
    try {
      const { CapacitorHttp } = await import("@capacitor/core");
      const res = await CapacitorHttp.request({
        method: params.method,
        url,
        headers: params.headers,
        data: params.body, // should be a POJO or string; plugin will handle JSON if header set
      });
      return res.data;
    } catch (_) {
      // plugin not available; fall back to axios below
    }
  }

  // Axios fallback (and default on web). Keep upload progress for POST with FormData.
  if (params.method === "POST") {
    return axios
      .post(url, params.body, {
        ...params,
        onUploadProgress: (progressEvent) => {
          const { loaded, total } = progressEvent || {};
          const percent = total ? Math.floor((loaded * 100) / total) : 0;
          onProgress(percent);
        },
      })
      .then((res) => res.data);
  }

  return axios(url, params).then((res) => res.data);
};

const API = {
  Test: {
    test: useTest,
  },
  Login: {
    login: login,
    postForgotPassword: postForgotPassword,
  },
};

export default API;
